<template>
  <div v-if="allowed" class="lab-page">
    <el-card class="lab-panel" shadow="never" :body-style="{ padding: '0', height: '100%' }">
      <div class="lab-heading">
        <div>
          <span class="lab-kicker">LABORATORY</span>
          <h1>当前背景图</h1>
        </div>
        <span class="lab-hint">拖动查看 · 滚轮或双指缩放</span>
      </div>

      <div
        ref="stage"
        class="image-stage"
        :class="{ 'is-dragging': dragging }"
        @wheel.prevent="onWheel"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerEnd"
        @pointercancel="onPointerEnd"
        @lostpointercapture="onPointerEnd"
        @dblclick="resetView"
      >
        <img
          v-if="backgroundUrl"
          class="background-image"
          :src="backgroundUrl"
          alt="当前网站背景图"
          :style="imageStyle"
          draggable="false"
          @dragstart.prevent
        >
        <div v-else class="image-empty">背景图加载中…</div>

        <div class="search-actions" aria-label="以图搜图">
          <button class="search-action" type="button" :disabled="!fileUrl || copying" @pointerdown.stop @dblclick.stop @click="copyImage">{{ copying ? '复制中…' : '复制图片' }}</button>
          <a class="search-action" :class="{ 'is-disabled': !fileUrl }" :href="fileUrl ? `${fileUrl}?download=true` : undefined" :tabindex="fileUrl ? 0 : -1" @pointerdown.stop @dblclick.stop>下载图片</a>
          <a
            v-for="service in searchServices"
            :key="service.label"
            class="search-action"
            :class="{ 'is-disabled': !service.href }"
            :href="service.href || undefined"
            :tabindex="service.href ? 0 : -1"
            target="_blank"
            rel="noopener noreferrer"
            @pointerdown.stop
            @dblclick.stop
          >{{ service.label }} ↗</a>
        </div>

        <div class="zoom-actions" aria-label="图片缩放">
          <button type="button" aria-label="缩小" :disabled="!backgroundUrl" @pointerdown.stop @click="zoomFromCenter(1 / 1.2)">−</button>
          <button type="button" class="zoom-value" :disabled="!backgroundUrl" title="恢复适合窗口" @pointerdown.stop @click="resetView">{{ Math.round(scale * 100) }}%</button>
          <button type="button" aria-label="放大" :disabled="!backgroundUrl" @pointerdown.stop @click="zoomFromCenter(1.2)">+</button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios'
export default {
  name: 'LabBackground',
  inject: ['backgroundOwner'],
  data() {
    return { scale: 1, offsetX: 0, offsetY: 0, dragging: false, copying: false, allowed: false }
  },
  computed: {
    backgroundUrl() { return this.backgroundOwner.background && this.backgroundOwner.background.url || '' },
    backgroundId() { return this.backgroundOwner.background && this.backgroundOwner.background.id || '' },
    fileUrl() { return this.backgroundUrl && /^\d+$/.test(String(this.backgroundId)) ? `/api/open/background/file/${this.backgroundId}` : '' },
    imageStyle() { return { transform: `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})` } },
    searchServices() {
      let url = ''
      if (this.backgroundUrl) {
        try {
          const parsed = new URL(this.backgroundUrl, window.location.href)
          if (['http:', 'https:'].includes(parsed.protocol)) url = parsed.href
        } catch (error) { /* A local image cannot be searched by URL. */ }
      }
      const encoded = encodeURIComponent(url)
      return [
        { label: 'ascii2d', href: url ? `https://ascii2d.net/search/url/${encoded}` : '' },
        { label: 'SauceNAO', href: url ? `https://saucenao.com/search.php?url=${encoded}` : '' }
      ]
    }
  },
  watch: { backgroundUrl() { this.resetView() } },
  created() {
    this.pointers = new Map()
    this.pinch = null
    this.checkAccess()
  },
  methods: {
    async checkAccess() {
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: 'Bearer ' + token } : {}
        const response = await axios.get('/api/open/lab/features', { headers })
        const feature = response.data.data.find(item => item.key === 'background')
        if (response.data.code === 200 && feature && feature.visible) { this.allowed = true; return }
      } catch (error) { /* Keep the viewer closed if settings cannot be loaded. */ }
      this.$message.warning('当前无法访问该实验室项目')
      this.$router.replace('/')
    },
    async asPng(blob) {
      if (blob.type === 'image/png') return blob
      const bitmap = await createImageBitmap(blob)
      try {
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height
        canvas.getContext('2d').drawImage(bitmap, 0, 0)
        return await new Promise((resolve, reject) => canvas.toBlob(result => result ? resolve(result) : reject(new Error('图片转换失败')), 'image/png'))
      } finally { bitmap.close() }
    },
    async copyImage() {
      if (!this.fileUrl || this.copying) return
      this.copying = true
      try {
        if (!navigator.clipboard || !navigator.clipboard.write || !window.ClipboardItem) throw new Error('不支持图片剪贴板')
        const image = fetch(this.fileUrl).then(response => {
          if (!response.ok) throw new Error('图片读取失败')
          return response.blob()
        }).then(blob => this.asPng(blob))
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': image })])
        this.$message.success('已复制图片')
      } catch (error) {
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(this.backgroundUrl)
          else {
            const input = document.createElement('textarea')
            input.value = this.backgroundUrl
            document.body.appendChild(input)
            input.select()
            const copied = document.execCommand('copy')
            input.remove()
            if (!copied) throw new Error('复制失败')
          }
          this.$message.info('无法复制图片，已复制图片链接')
        } catch (copyError) { this.$message.error('复制失败，请检查浏览器剪贴板权限') }
      } finally { this.copying = false }
    },
    resetView() {
      this.scale = 1
      this.offsetX = 0
      this.offsetY = 0
    },
    zoomFromCenter(factor) { this.zoomTo(this.scale * factor) },
    zoomTo(nextScale, clientX, clientY) {
      const next = Math.max(0.25, Math.min(6, nextScale))
      const rect = this.$refs.stage.getBoundingClientRect()
      const x = (clientX == null ? rect.left + rect.width / 2 : clientX) - rect.left - rect.width / 2
      const y = (clientY == null ? rect.top + rect.height / 2 : clientY) - rect.top - rect.height / 2
      const ratio = next / this.scale
      this.offsetX = x - (x - this.offsetX) * ratio
      this.offsetY = y - (y - this.offsetY) * ratio
      this.scale = next
    },
    onWheel(event) {
      if (this.backgroundUrl) this.zoomTo(this.scale * (event.deltaY < 0 ? 1.12 : 1 / 1.12), event.clientX, event.clientY)
    },
    pinchMetrics() {
      const points = Array.from(this.pointers.values())
      if (points.length < 2) return null
      return {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
        distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
      }
    },
    onPointerDown(event) {
      if (!this.backgroundUrl || (event.pointerType === 'mouse' && event.button !== 0)) return
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
      this.dragging = true
      this.pinch = this.pinchMetrics()
    },
    onPointerMove(event) {
      const previous = this.pointers.get(event.pointerId)
      if (!previous) return
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
      if (this.pointers.size === 1) {
        this.offsetX += event.clientX - previous.x
        this.offsetY += event.clientY - previous.y
        return
      }
      const current = this.pinchMetrics()
      if (current && this.pinch) {
        if (this.pinch.distance) this.zoomTo(this.scale * current.distance / this.pinch.distance, current.x, current.y)
        this.offsetX += current.x - this.pinch.x
        this.offsetY += current.y - this.pinch.y
      }
      this.pinch = current
    },
    onPointerEnd(event) {
      this.pointers.delete(event.pointerId)
      this.dragging = this.pointers.size > 0
      this.pinch = this.pinchMetrics()
    }
  }
}
</script>

<style scoped>
.lab-page { height: 100%; min-height: 0; }
.lab-panel { height: 100%; border: 1px solid rgba(170, 190, 210, .36); border-radius: 16px; background: rgba(255, 255, 255, .67); backdrop-filter: blur(18px); overflow: hidden; }
.lab-heading { height: 83px; box-sizing: border-box; padding: 19px 22px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid rgba(170, 190, 210, .3); }
.lab-kicker { display: block; color: #409eff; font-size: 10px; font-weight: 700; letter-spacing: .16em; }
.lab-heading h1 { margin: 5px 0 0; font-size: 21px; line-height: 1.15; color: #303133; }
.lab-hint { color: #909399; font-size: 12px; white-space: nowrap; }
.image-stage { position: relative; display: flex; align-items: center; justify-content: center; height: calc(100% - 83px); min-height: 0; overflow: hidden; background: rgba(37, 48, 63, .82); touch-action: none; cursor: grab; }
.image-stage.is-dragging { cursor: grabbing; }
.background-image { display: block; max-width: 100%; max-height: 100%; object-fit: contain; pointer-events: none; will-change: transform; }
.image-empty { color: rgba(255,255,255,.78); font-size: 14px; }
.search-actions, .zoom-actions { position: absolute; z-index: 1; display: flex; gap: 7px; border-radius: 10px; padding: 7px; background: rgba(19, 29, 43, .78); backdrop-filter: blur(10px); }
.search-actions { top: 14px; right: 14px; flex-wrap: wrap; justify-content: flex-end; max-width: calc(100% - 28px); }
.search-action, .zoom-actions button { border: 1px solid rgba(255,255,255,.22); border-radius: 6px; background: rgba(255,255,255,.1); color: #fff; font: inherit; font-size: 12px; line-height: 20px; padding: 4px 8px; text-decoration: none; cursor: pointer; }
.search-action:hover, .zoom-actions button:hover:not(:disabled) { background: rgba(64, 158, 255, .6); }
.search-action.is-disabled, .search-action:disabled, .zoom-actions button:disabled { opacity: .45; cursor: default; pointer-events: none; }
.zoom-actions { right: 14px; bottom: 14px; }
.zoom-actions button { min-width: 32px; }
.zoom-actions .zoom-value { min-width: 56px; }
@media (max-width: 767px) {
  .lab-heading { height: 72px; padding: 14px; }
  .lab-heading h1 { font-size: 18px; }
  .lab-hint { display: none; }
  .image-stage { height: calc(100% - 72px); }
  .search-actions { top: 10px; right: 10px; left: 10px; justify-content: center; }
  .zoom-actions { right: 10px; bottom: 10px; }
}
</style>
